import type { ReactElement } from 'react';
import { createContext, useContext, useEffect, useReducer } from 'react';

import type { GenericDetails } from 'components/Input/Autocomplete';

import type { CountryList } from '../hooks/api/general/useGetCountryList';
import type { SelectProjectsResponse } from '../hooks/api/general/useGetSelectProjects';
import type { ProjectNames, RoleNames } from '../hooks/permission/types';
import {
    SET_COUNTRY_LIST,
    SET_PAGINATION,
    SET_PERMISSION_AVAILABLE_PROJECTS,
    SET_PERMISSION_AVAILABLE_ROLES,
    SET_PERMISSION_GROUPS,
    SET_PERMISSION_PRIORITY,
    SET_PROJECTS_LIST,
} from './actions/generalData';
import { ActionType } from './actions/preview';
import { LOG_IN, LOG_OUT } from './actions/user';
import type { RootState } from './reducers';
import rootReducer from './reducers';
import { initialState as generalDataInitialState } from './reducers/generalData';
import { initialState as previewInitialState } from './reducers/preview';
import { initialState as toolbarInitialState } from './reducers/toolbar';
import { initialState as userInitialState } from './reducers/user';
import { GENERATE_TOOLBAR_FIELDS, SET_TOOLBAR_VALUE } from './types/toolbar';
import type { Language } from '@welltech/wellfunnel/types';

const initialState: RootState = {
    toolbar: toolbarInitialState,
    user: userInitialState,
    generalData: generalDataInitialState,
    preview: previewInitialState,
};

const AppContext = createContext<
    | {
          appState: RootState;
          setProjectsList: (projectsList: SelectProjectsResponse[]) => void;
          setCountryList: (countryList: CountryList[]) => void;
          setPagination: (pageSize: number) => void;
          setPermissionGroups: (permissionGroups: Record<string, { role: RoleNames }>) => void;
          setPermissionAvailableRoles: (permissionAvailableRoles: RoleNames[]) => void;
          setPermissionAvailableProjects: (permissionAvailableProjects: ProjectNames[]) => void;
          setPermissionPriority: (permissionPriority: { rolePriority: RoleNames; projectPriority: string }) => void;
          generateToolbarFields: (toolbarValues: { name: string; value: Record<string, GenericDetails> }) => void;
          setToolbarValue: (toolbarFieldValue: { name: string; fieldName: string; fieldValue: GenericDetails }) => void;
          logIn: (user: { group: any; token: string; email: any }) => void;
          logOut: () => void;
          setStandardPreviewParams: (standardPreviewParams: Record<string, string | number | string[] | number[] | undefined>) => void;
          setRulesBasedPreviewParams: (rulesBasedPreviewParams: Record<string, (string | number | string[] | number[])[]>) => void;
          resetPreviewParams: () => void;
          setPreviewLocalization: (localization: Language) => void;
          setTimerValue: (value: string) => void;
      }
    | undefined
>(undefined);
const AppProvider = ({ children }: { children: ReactElement }) => {
    const [state, dispatch] = useReducer(rootReducer, initialState, (initial) => {
        const savedState = localStorage.getItem('appState');
        return savedState ? ({ ...JSON.parse(savedState), preview: initialState.preview } as RootState) : initial;
    });

    useEffect(() => {
        localStorage.setItem('appState', JSON.stringify(state));
    }, [state]);

    const setProjectsList = (projectsList: SelectProjectsResponse[]): void => {
        dispatch({ type: SET_PROJECTS_LIST, payload: projectsList });
    };

    const setCountryList = (countryList: CountryList[]) => {
        dispatch({ type: SET_COUNTRY_LIST, payload: countryList });
    };

    const setPagination = (pageSize: number) => {
        dispatch({ type: SET_PAGINATION, payload: pageSize });
    };

    const setPermissionGroups = (permissionGroups: Record<string, { role: RoleNames }>) => {
        dispatch({ type: SET_PERMISSION_GROUPS, payload: permissionGroups });
    };

    const setPermissionAvailableRoles = (permissionAvailableRoles: RoleNames[]) => {
        dispatch({ type: SET_PERMISSION_AVAILABLE_ROLES, payload: permissionAvailableRoles });
    };

    const setPermissionAvailableProjects = (permissionAvailableProjects: ProjectNames[]) => {
        dispatch({ type: SET_PERMISSION_AVAILABLE_PROJECTS, payload: permissionAvailableProjects });
    };

    const setPermissionPriority = (permissionPriority: { rolePriority: RoleNames; projectPriority: string }) => {
        dispatch({ type: SET_PERMISSION_PRIORITY, payload: permissionPriority });
    };

    const generateToolbarFields = (toolbarValues: { name: string; value: Record<string, GenericDetails> }) => {
        dispatch({ type: GENERATE_TOOLBAR_FIELDS, payload: toolbarValues });
    };

    const setToolbarValue = (toolbarFieldValue: { name: string; fieldName: string; fieldValue: GenericDetails }) => {
        dispatch({ type: SET_TOOLBAR_VALUE, payload: toolbarFieldValue });
    };

    const logIn = (user: { group: any; token: string; email: any }) => {
        dispatch({ type: LOG_IN, payload: user });
    };

    const logOut = () => {
        dispatch({ type: LOG_OUT });
    };

    const setStandardPreviewParams = (params: Record<string, string | number | string[] | number[] | undefined>) => {
        dispatch({ type: ActionType.SET_STANDARD_PREVIEW_PARAMS, payload: params });
    };

    const setRulesBasedPreviewParams = (params: Record<string, (string | number | string[] | number[])[]>) => {
        dispatch({ type: ActionType.SET_RULES_BASED_PREVIEW_PARAMS, payload: params });
    };

    const resetPreviewParams = () => {
        dispatch({ type: ActionType.RESET_PREVIEW_PARAMS });
    };

    const setPreviewLocalization = (localization: Language) => {
        dispatch({ type: ActionType.SET_PREVIEW_LOCALIZATION, payload: localization });
    };

    const setTimerValue = (timerValue: string) => {
        dispatch({ type: ActionType.SET_TIMER_VALUE, payload: timerValue });
    };

    return (
        <AppContext.Provider
            value={{
                appState: state,
                setProjectsList,
                setCountryList,
                setPagination,
                setPermissionGroups,
                setPermissionAvailableRoles,
                setPermissionAvailableProjects,
                setPermissionPriority,
                generateToolbarFields,
                setToolbarValue,
                logIn,
                logOut,
                setStandardPreviewParams,
                setRulesBasedPreviewParams,
                resetPreviewParams,
                setPreviewLocalization,
                setTimerValue,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

export default AppProvider;
